import { supabase } from '../lib/supabase';
import { format, parseISO, startOfMonth } from 'date-fns';

export class CampaignManagementService {
  /**
   * Get all campaigns with filters
   */
  static async getCampaigns(filters = {}) {
    try {
      let query = supabase
        ?.from('campaigns')
        ?.select(`
          id,
          name,
          description,
          status,
          start_date,
          end_date,
          target_scans,
          created_at,
          updated_at,
          owner_id
        `);

      // Apply status filter
      if (filters?.status && filters?.status !== 'all') {
        query = query?.eq('status', filters?.status);
      }

      // Apply date range filter - Fix timestamp conversion
      if (filters?.dateRange && filters?.dateRange !== 'all') {
        const dateFilter = this.getDateRangeFilter(filters?.dateRange);
        if (dateFilter) {
          query = query?.gte('created_at', dateFilter);
        }
      }

      const { data, error } = await query?.order('created_at', { ascending: false });

      if (error) {
        console.error('Campaign fetch error:', error);
        throw error;
      }

      // Get QR codes count and scan metrics for each campaign
      const campaignsWithMetrics = await Promise.all(
        (data || [])?.map(async (campaign) => {
          try {
            // Get QR codes count for this campaign
            const { count: qrCodesCount } = await supabase
              ?.from('qr_codes')
              ?.select('*', { count: 'exact', head: true })
              ?.eq('campaign_id', campaign?.id);

            // Get total scans for this campaign
            let totalScans = 0;
            let uniqueScans = 0;
            let totalConversions = 0;

            try {
              // Try to get analytics from analytics_daily table
              const { data: analyticsData, error: analyticsError } = await supabase
                ?.from('analytics_daily')
                ?.select('total_scans, unique_scans, total_conversions')
                ?.eq('campaign_id', campaign?.id);

              if (!analyticsError && Array.isArray(analyticsData) && analyticsData?.length > 0) {
                totalScans = analyticsData?.reduce((sum, row) => sum + (row?.total_scans || 0), 0);
                uniqueScans = analyticsData?.reduce((sum, row) => sum + (row?.unique_scans || 0), 0);
                totalConversions = analyticsData?.reduce((sum, row) => sum + (row?.total_conversions || 0), 0);
              } else {
                // Fallback to direct scan counting - Fix the nested query issue
                const { data: qrCodesForCampaign } = await supabase
                  ?.from('qr_codes')
                  ?.select('id')
                  ?.eq('campaign_id', campaign?.id);

                if (Array.isArray(qrCodesForCampaign) && qrCodesForCampaign?.length > 0) {
                  const qrCodeIds = qrCodesForCampaign?.map(qr => qr?.id);
                  
                  const { data: scanData } = await supabase
                    ?.from('qr_scans')
                    ?.select('converted, is_unique_user')
                    ?.in('qr_code_id', qrCodeIds);

                  if (Array.isArray(scanData)) {
                    totalScans = scanData?.length;
                    uniqueScans = scanData?.filter(scan => scan?.is_unique_user)?.length || 0;
                    totalConversions = scanData?.filter(scan => scan?.converted)?.length || 0;
                  }
                }
              }
            } catch (scanError) {
              console.log(`Scan metrics error for campaign ${campaign?.id}:`, scanError);
              // Set default values on error
              totalScans = 0;
              uniqueScans = 0;
              totalConversions = 0;
            }

            // Calculate progress percentage
            const progressPercentage = campaign?.target_scans && campaign?.target_scans > 0 
              ? Math.min(Math.round((totalScans / campaign?.target_scans) * 100), 100)
              : 0;

            // Calculate conversion rate
            const conversionRate = totalScans > 0 
              ? ((totalConversions / totalScans) * 100)?.toFixed(1)
              : '0.0';

            // Determine campaign status based on dates and metrics
            const campaignStatus = this.getCampaignStatus(campaign);

            return {
              ...campaign,
              qr_codes_count: qrCodesCount || 0,
              total_scans: totalScans,
              unique_scans: uniqueScans,
              total_conversions: totalConversions,
              conversion_rate: `${conversionRate}%`,
              progress_percentage: progressPercentage,
              computed_status: campaignStatus,
              formatted_start_date: campaign?.start_date 
                ? format(parseISO(campaign?.start_date), 'MMM dd, yyyy')
                : 'Not set',
              formatted_end_date: campaign?.end_date 
                ? format(parseISO(campaign?.end_date), 'MMM dd, yyyy')
                : 'Not set'
            };
          } catch (campaignError) {
            console.error(`Error processing campaign ${campaign?.id}:`, campaignError);
            
            // Return campaign with default values on error
            return {
              ...campaign,
              qr_codes_count: 0,
              total_scans: 0,
              unique_scans: 0,
              total_conversions: 0,
              conversion_rate: '0.0%',
              progress_percentage: 0,
              computed_status: 'error',
              formatted_start_date: 'Error',
              formatted_end_date: 'Error',
              error: campaignError?.message
            };
          }
        })
      );

      return {
        data: campaignsWithMetrics,
        error: null
      };
    } catch (error) {
      console.error('Campaign service error:', error);
      return {
        data: [],
        error: error?.message || 'Failed to fetch campaigns'
      };
    }
  }

  /**
   * Get campaign statistics for dashboard
   */
  static async getCampaignStats() {
    try {
      // Get total campaigns by status
      const { data: campaigns, error: campaignsError } = await supabase
        ?.from('campaigns')
        ?.select('status');

      if (campaignsError) throw campaignsError;

      // Count by status - Add safety check for array
      const statusCounts = Array.isArray(campaigns) 
        ? campaigns?.reduce((acc, campaign) => {
            acc[campaign?.status] = (acc?.[campaign?.status] || 0) + 1;
            return acc;
          }, {})
        : {};

      // Get active campaigns performance
      const { data: activeCampaigns, error: activeError } = await supabase
        ?.from('campaigns')
        ?.select(`
          id,
          target_scans,
          analytics_daily!inner(total_scans)
        `)
        ?.eq('status', 'active');

      if (activeError) {
        console.log('Active campaigns error:', activeError);
      }

      // Calculate average performance - Add safety checks
      let avgPerformance = 0;
      if (Array.isArray(activeCampaigns) && activeCampaigns?.length > 0) {
        const performanceScores = activeCampaigns?.map(campaign => {
          if (Array.isArray(campaign?.analytics_daily)) {
            let totalScans = campaign?.analytics_daily?.reduce((sum, day) => sum + (day?.total_scans || 0), 0);
            return campaign?.target_scans > 0 ? (totalScans / campaign?.target_scans) * 100 : 0;
          }
          return 0;
        });
        
        if (Array.isArray(performanceScores) && performanceScores?.length > 0) {
          avgPerformance = performanceScores?.reduce((sum, score) => sum + score, 0) / performanceScores?.length;
        }
      }

      return {
        data: {
          total: Array.isArray(campaigns) ? campaigns?.length : 0,
          active: statusCounts?.active || 0,
          paused: statusCounts?.paused || 0,
          completed: statusCounts?.completed || 0,
          draft: statusCounts?.draft || 0,
          archived: statusCounts?.archived || 0,
          avg_performance: Math.round(avgPerformance) || 0
        },
        error: null
      };
    } catch (error) {
      console.error('Campaign stats error:', error);
      return {
        data: {
          total: 0,
          active: 0,
          paused: 0,
          completed: 0,
          draft: 0,
          archived: 0,
          avg_performance: 0
        },
        error: error?.message
      };
    }
  }

  /**
   * Get campaign analytics summary for dashboard
   */
  static async getCampaignAnalyticsSummary(dateRange = 'last30d') {
    try {
      const dateFilter = this.getDateRangeFilter(dateRange);

      // Get campaign summary stats
      const { data: campaigns, error: campaignsError } = await supabase
        ?.from('campaigns')
        ?.select('id, status, target_scans');

      if (campaignsError) throw campaignsError;

      // Count by status - Add safety check
      const totalCampaigns = Array.isArray(campaigns) ? campaigns?.length : 0;
      const activeCampaigns = Array.isArray(campaigns) ? campaigns?.filter(c => c?.status === 'active')?.length : 0;
      const pausedCampaigns = Array.isArray(campaigns) ? campaigns?.filter(c => c?.status === 'paused')?.length : 0;
      const completedCampaigns = Array.isArray(campaigns) ? campaigns?.filter(c => c?.status === 'completed')?.length : 0;

      // Get total scans from analytics_daily
      const { data: analyticsData, error: analyticsError } = await supabase
        ?.from('analytics_daily')
        ?.select('total_scans, total_conversions')
        ?.gte('date', dateFilter);

      let totalScans = 0;
      let totalConversions = 0;
      if (!analyticsError && Array.isArray(analyticsData) && analyticsData?.length > 0) {
        totalScans = analyticsData?.reduce((sum, row) => sum + (row?.total_scans || 0), 0);
        totalConversions = analyticsData?.reduce((sum, row) => sum + (row?.total_conversions || 0), 0);
      }

      // Calculate average conversion rate
      const avgConversionRate = totalScans > 0 
        ? parseFloat(((totalConversions / totalScans) * 100)?.toFixed(1))
        : 0;

      // Calculate total target scans - Add safety check
      const totalTargetScans = Array.isArray(campaigns) 
        ? campaigns?.reduce((sum, campaign) => sum + (campaign?.target_scans || 0), 0)
        : 0;

      // Calculate progress percentage
      const progressPercentage = totalTargetScans > 0 
        ? Math.min(Math.round((totalScans / totalTargetScans) * 100), 100)
        : 0;

      return {
        data: {
          totalCampaigns,
          activeCampaigns,
          pausedCampaigns,
          completedCampaigns,
          totalScans,
          totalConversions,
          avgConversionRate,
          progressPercentage,
          totalTargetScans
        },
        error: null
      };
    } catch (error) {
      console.error('Campaign analytics summary error:', error);
      return {
        data: {
          totalCampaigns: 0,
          activeCampaigns: 0,
          pausedCampaigns: 0,
          completedCampaigns: 0,
          totalScans: 0,
          totalConversions: 0,
          avgConversionRate: 0,
          progressPercentage: 0,
          totalTargetScans: 0
        },
        error: error?.message
      };
    }
  }

  /**
   * Get campaign timeline data
   */
  static async getCampaignTimeline(limit = 20) {
    try {
      // Get recent campaign activities
      const { data: campaigns, error: campaignsError } = await supabase
        ?.from('campaigns')
        ?.select(`
          id,
          name,
          status,
          created_at,
          updated_at,
          start_date,
          end_date
        `)
        ?.order('updated_at', { ascending: false })
        ?.limit(limit);

      if (campaignsError) throw campaignsError;

      // Format timeline data
      const timelineData = (campaigns || [])?.map(campaign => {
        let activity = 'created';
        let timestamp = campaign?.created_at;

        // Determine activity type based on dates and status
        if (campaign?.status === 'active' && campaign?.start_date) {
          activity = 'started';
          timestamp = campaign?.start_date;
        } else if (campaign?.status === 'completed' && campaign?.end_date) {
          activity = 'completed';
          timestamp = campaign?.end_date;
        } else if (campaign?.status === 'paused') {
          activity = 'paused';
          timestamp = campaign?.updated_at;
        } else if (campaign?.status === 'archived') {
          activity = 'archived';
          timestamp = campaign?.updated_at;
        }

        return {
          id: campaign?.id,
          campaignName: campaign?.name,
          activity,
          timestamp,
          status: campaign?.status,
          description: `Campaign "${campaign?.name}" was ${activity}`
        };
      });

      // Sort by timestamp (most recent first)
      timelineData?.sort((a, b) => new Date(b?.timestamp) - new Date(a?.timestamp));

      return {
        data: timelineData || [],
        error: null
      };
    } catch (error) {
      console.error('Campaign timeline error:', error);
      return {
        data: [],
        error: error?.message
      };
    }
  }

  /**
   * Get A/B testing data
   */
  static async getABTestingData() {
    try {
      // Get campaigns with multiple QR codes (potential A/B tests)
      const { data: campaigns, error: campaignsError } = await supabase
        ?.from('campaigns')
        ?.select(`
          id,
          name,
          status,
          qr_codes!inner(
            id,
            name,
            created_at
          )
        `)
        ?.eq('status', 'active');

      if (campaignsError) throw campaignsError;

      // Filter campaigns with multiple QR codes
      const abTestCampaigns = (campaigns || [])?.filter(campaign => 
        campaign?.qr_codes?.length > 1
      );

      // Get analytics for each QR code in A/B test campaigns
      const abTestData = await Promise.all(
        abTestCampaigns?.map(async (campaign) => {
          try {
            const qrCodeIds = campaign?.qr_codes?.map(qr => qr?.id) || [];
            
            const { data: analyticsData, error: analyticsError } = await supabase
              ?.from('analytics_daily')
              ?.select(`
                qr_code_id,
                total_scans,
                total_conversions,
                unique_scans
              `)
              ?.in('qr_code_id', qrCodeIds);

            if (analyticsError) {
              console.log(`Analytics error for campaign ${campaign?.id}:`, analyticsError);
              return null;
            }

            // Aggregate data by QR code
            const qrPerformance = campaign?.qr_codes?.map(qr => {
              const qrAnalytics = (analyticsData || [])?.filter(a => a?.qr_code_id === qr?.id);
              let totalScans = qrAnalytics?.reduce((sum, a) => sum + (a?.total_scans || 0), 0);
              let totalConversions = qrAnalytics?.reduce((sum, a) => sum + (a?.total_conversions || 0), 0);
              let uniqueScans = qrAnalytics?.reduce((sum, a) => sum + (a?.unique_scans || 0), 0);
              
              const conversionRate = totalScans > 0 
                ? parseFloat(((totalConversions / totalScans) * 100)?.toFixed(1))
                : 0;

              return {
                qrCodeId: qr?.id,
                qrCodeName: qr?.name,
                totalScans,
                totalConversions,
                uniqueScans,
                conversionRate
              };
            });

            // Calculate winner
            const winner = qrPerformance?.reduce((prev, current) => 
              (current?.conversionRate > prev?.conversionRate) ? current : prev
            );

            return {
              campaignId: campaign?.id,
              campaignName: campaign?.name,
              status: campaign?.status,
              variants: qrPerformance?.length || 0,
              winner: winner?.qrCodeName || 'None',
              winnerConversionRate: winner?.conversionRate || 0,
              totalScans: qrPerformance?.reduce((sum, qr) => sum + (qr?.totalScans || 0), 0),
              performance: qrPerformance || []
            };
          } catch (error) {
            console.log(`Error processing A/B test data for campaign ${campaign?.id}:`, error);
            return null;
          }
        }) || []
      );

      // Filter out null results
      const validAbTestData = abTestData?.filter(data => data !== null) || [];

      return {
        data: validAbTestData,
        error: null
      };
    } catch (error) {
      console.error('A/B testing data error:', error);
      return {
        data: [],
        error: error?.message
      };
    }
  }

  /**
   * Create a new campaign
   */
  static async createCampaign(campaignData) {
    try {
      const { data, error } = await supabase
        ?.from('campaigns')
        ?.insert([{
          ...campaignData,
          created_at: new Date()?.toISOString(),
          updated_at: new Date()?.toISOString()
        }])
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        data,
        error: null
      };
    } catch (error) {
      console.error('Create campaign error:', error);
      return {
        data: null,
        error: error?.message || 'Failed to create campaign'
      };
    }
  }

  /**
   * Update an existing campaign
   */
  static async updateCampaign(id, updates) {
    try {
      const { data, error } = await supabase
        ?.from('campaigns')
        ?.update({
          ...updates,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', id)
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        data,
        error: null
      };
    } catch (error) {
      console.error('Update campaign error:', error);
      return {
        data: null,
        error: error?.message || 'Failed to update campaign'
      };
    }
  }

  /**
   * Delete a campaign
   */
  static async deleteCampaign(id) {
    try {
      const { error } = await supabase
        ?.from('campaigns')
        ?.delete()
        ?.eq('id', id);

      if (error) throw error;

      return {
        error: null
      };
    } catch (error) {
      console.error('Delete campaign error:', error);
      return {
        error: error?.message || 'Failed to delete campaign'
      };
    }
  }

  /**
   * Get campaign performance data for charts
   */
  static async getCampaignPerformance(campaignId, dateRange = 'last30d') {
    try {
      const dateFilter = this.getDateRangeFilter(dateRange);

      const { data, error } = await supabase
        ?.from('analytics_daily')
        ?.select('date, total_scans, unique_scans, total_conversions')
        ?.eq('campaign_id', campaignId)
        ?.gte('date', dateFilter)
        ?.order('date', { ascending: true });

      if (error) throw error;

      // Format data for charts
      const chartData = (data || [])?.map(row => ({
        date: format(parseISO(row?.date), 'MMM dd'),
        scans: row?.total_scans || 0,
        uniqueScans: row?.unique_scans || 0,
        conversions: row?.total_conversions || 0
      }));

      return {
        data: chartData,
        error: null
      };
    } catch (error) {
      console.error('Campaign performance error:', error);
      return {
        data: [],
        error: error?.message
      };
    }
  }

  /**
   * Bulk update campaigns
   */
  static async bulkUpdateCampaigns(campaignIds, updates) {
    try {
      const { data, error } = await supabase
        ?.from('campaigns')
        ?.update({
          ...updates,
          updated_at: new Date()?.toISOString()
        })
        ?.in('id', campaignIds)
        ?.select();

      if (error) throw error;

      return {
        data,
        error: null
      };
    } catch (error) {
      console.error('Bulk update error:', error);
      return {
        data: null,
        error: error?.message || 'Failed to bulk update campaigns'
      };
    }
  }

  // Helper methods
  static getDateRangeFilter(dateRange) {
    const now = new Date();
    
    switch (dateRange) {
      case 'today':
        return startOfMonth(now)?.toISOString();
      case 'last7d':
        return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)?.toISOString();
      case 'last30d':
        return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)?.toISOString();
      case 'last90d':
        return new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000)?.toISOString();
      case 'thisMonth':
        return startOfMonth(now)?.toISOString();
      case 'lastMonth':
        const lastMonth = new Date(now);
        lastMonth?.setMonth(lastMonth?.getMonth() - 1);
        return startOfMonth(lastMonth)?.toISOString();
      default:
        return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)?.toISOString();
    }
  }

  static getCampaignStatus(campaign) {
    const now = new Date();
    const startDate = campaign?.start_date ? parseISO(campaign?.start_date) : null;
    const endDate = campaign?.end_date ? parseISO(campaign?.end_date) : null;

    // Check database status first
    if (campaign?.status === 'archived' || campaign?.status === 'completed') {
      return campaign?.status;
    }

    // Determine status based on dates
    if (startDate && startDate > now) {
      return 'scheduled';
    } else if (endDate && endDate < now) {
      return 'expired';
    } else if (campaign?.status === 'active') {
      return 'active';
    } else {
      return campaign?.status || 'draft';
    }
  }

  /**
   * Get campaigns for dropdown/selector
   */
  static async getCampaignOptions() {
    try {
      const { data, error } = await supabase
        ?.from('campaigns')
        ?.select('id, name, status')
        ?.in('status', ['active', 'draft', 'paused'])
        ?.order('name');

      if (error) throw error;

      return {
        data: data || [],
        error: null
      };
    } catch (error) {
      return {
        data: [],
        error: error?.message
      };
    }
  }
}